import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateGalleryFolderInput,
  GalleryFolder,
  GalleryFolderList,
  UpdateGalleryFolderInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import type { Media } from '../media/media-contract.js';
import {
  createGalleryFolder,
  deleteGalleryFolder,
  listGalleryFolders,
  listGalleryMedia,
  moveMediaToFolder,
  updateGalleryFolder,
  type MoveMediaToFolderInput,
} from './gallery-api.js';

/**
 * The gallery hooks.
 *
 * `useFolderMedia` is fetched a PAGE at a time rather than with a growing page size, unlike most
 * lists in this app: a folder can hold hundreds of images, and the whole point of paging it is
 * that "Load more" costs one 24-image request rather than re-asking for everything seen so far
 * at a larger size.
 *
 * EVERY WRITE — a folder created, renamed, deleted, or a batch of images moved — invalidates the
 * whole `gallery` namespace through the one helper below. A move changes the `mediaCount` on two
 * folders at once (the one an image left, the one it landed in) and the image lists both folders
 * show, so nothing here tries to guess which of those to patch by hand.
 */

const FOLDER_MEDIA_PAGE_SIZE = 24;

export const galleryKeys = {
  all: ['gallery'] as const,
  folders: () => ['gallery', 'folders'] as const,
  media: (scope: string, pageSize: number) => ['gallery', 'media', scope, pageSize] as const,
};

export function useGalleryFolders(): UseQueryResult<GalleryFolderList> {
  const client = useApiClient();
  return useQuery({
    queryKey: galleryKeys.folders(),
    queryFn: () => listGalleryFolders(client),
  });
}

export interface UseFolderMediaOptions {
  pageSize?: number;
}

/**
 * One folder's images, or the unfiled ones — a page at a time.
 *
 * `scope` is a folder id, the literal `'unfiled'`, or null while nobody has chosen either yet.
 * Null is `enabled: false`, not a request for an empty folder.
 */
export function useFolderMedia(
  scope: string | null,
  options: UseFolderMediaOptions = {},
): UseInfiniteQueryResult<InfiniteData<Media[]>> {
  const client = useApiClient();
  const pageSize = options.pageSize ?? FOLDER_MEDIA_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: galleryKeys.media(scope ?? '', pageSize),
    queryFn: ({ pageParam }: { pageParam: number }) =>
      listGalleryMedia(client, {
        ...(scope === 'unfiled' ? { unfiled: true } : { folderId: scope as string }),
        page: pageParam,
        pageSize,
      }),
    initialPageParam: 1,
    // No total comes back on this envelope — a full page is the only signal there may be more.
    getNextPageParam: (lastPage: Media[], allPages: Media[][]) =>
      lastPage.length === pageSize ? allPages.length + 1 : undefined,
    enabled: scope !== null,
  });
}

/**
 * Invalidate what any gallery write can have changed: the folder tree — names, counts, the
 * tree shape — and every open folder's images. One helper, used by every mutation below, so
 * there is no write that refreshes only half the screen.
 */
function useGalleryInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: galleryKeys.all });
  };
}

export function useCreateGalleryFolder(): UseMutationResult<
  GalleryFolder,
  unknown,
  CreateGalleryFolderInput
> {
  const client = useApiClient();
  const invalidate = useGalleryInvalidation();
  return useMutation({
    mutationFn: (input: CreateGalleryFolderInput) => createGalleryFolder(client, input),
    onSuccess: invalidate,
  });
}

export function useUpdateGalleryFolder(): UseMutationResult<
  GalleryFolder,
  unknown,
  { folderId: string; patch: UpdateGalleryFolderInput }
> {
  const client = useApiClient();
  const invalidate = useGalleryInvalidation();
  return useMutation({
    mutationFn: ({ folderId, patch }: { folderId: string; patch: UpdateGalleryFolderInput }) =>
      updateGalleryFolder(client, folderId, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteGalleryFolder(): UseMutationResult<void, unknown, string> {
  const client = useApiClient();
  const invalidate = useGalleryInvalidation();
  return useMutation({
    mutationFn: (folderId: string) => deleteGalleryFolder(client, folderId),
    onSuccess: invalidate,
  });
}

export function useMoveMediaToFolder(): UseMutationResult<
  Media[],
  unknown,
  MoveMediaToFolderInput
> {
  const client = useApiClient();
  const invalidate = useGalleryInvalidation();
  return useMutation({
    mutationFn: (input: MoveMediaToFolderInput) => moveMediaToFolder(client, input),
    onSuccess: invalidate,
  });
}
